import { zodResolver } from '@hookform/resolvers/zod'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Typography, { typography } from '@repo/ui/typography'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { EnrollmentInput } from '../../../../lib/types/models/enrollment/enrollment-input'
import { BaseFormProps } from '../../../../lib/types/utils'
import { getSubmitAction } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { createStringSchema } from '../../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledInput from '../../../elements/inputs/controlled-input'
import ValidationButtons from '../../../elements/validation-buttons'

export type MedicalInformationInput = Pick<
  EnrollmentInput,
  | 'medicalInformations'
  | 'medicalInformationsEn'
  | 'additionalMedicalInformations'
  | 'additionalMedicalInformationsEn'
>

const formSchema: ZodType<MedicalInformationInput> = z.object({
  medicalInformations: createStringSchema('Information médicale', { nullish: true }),
  medicalInformationsEn: createStringSchema('Traduction', { nullish: true }),
  additionalMedicalInformations: createStringSchema('Information complémentaire', {
    nullish: true
  }),
  additionalMedicalInformationsEn: createStringSchema('Traduction', { nullish: true })
})

type Props = BaseFormProps<MedicalInformationInput, true> & {
  // the backend decides whether LEC may still write the main health field
  canEditMainMedicalInformations: boolean
}

const MedicalInformationForm = ({
  defaultValues,
  className,
  canEditMainMedicalInformations,
  ...validationButtonProps
}: Props) => {
  const form = useForm<MedicalInformationInput>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })
  const [medicalInformations, additionalMedicalInformations] = form.watch([
    'medicalInformations',
    'additionalMedicalInformations'
  ])

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={`${className ?? ''} flex flex-col gap-4`}>
          {(canEditMainMedicalInformations || medicalInformations?.trim()) && (
            <div className="flex flex-col gap-3">
              <Typography variant="label-medium">Informations médicales</Typography>
              {canEditMainMedicalInformations ? (
                <FormField
                  control={form.control}
                  name="medicalInformations"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <ControlledInput
                          {...field}
                          type="textarea"
                          label="Information sur la santé"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              ) : (
                <Typography variant="tag">{medicalInformations}</Typography>
              )}
              <FormField
                control={form.control}
                name="medicalInformationsEn"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledInput
                        {...field}
                        type="textarea"
                        placeholder="Informations médicales"
                        style={{
                          ...typography['small-medium'],
                          color: tailwindColors.grey.light.active
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          )}

          <div className="flex flex-col gap-2">
            <Typography variant="label-medium">Information complémentaire</Typography>
            <FormField
              control={form.control}
              name="additionalMedicalInformations"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledInput
                      {...field}
                      type="textarea"
                      label="Information complémentaire"
                      value={field.value}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            {additionalMedicalInformations?.trim() && (
              // Unregistered while hidden: a translation typed before the information was emptied must
              // not be saved without the information it translates.
              <FormField
                control={form.control}
                name="additionalMedicalInformationsEn"
                shouldUnregister
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledInput
                        {...field}
                        type="textarea"
                        label="Traduction"
                        value={field.value}
                        style={{
                          ...typography['small-medium'],
                          color: tailwindColors.grey.light.active
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            )}
          </div>
        </div>
        <ValidationButtons hasBorderTop {...validationButtonProps} />
      </form>
    </Form>
  )
}

export default MedicalInformationForm
